/**
 * RiskManagementPage.js - Risco, Fraude e Disciplina
 * BLOCO 4: Controlo de Risco e Compliance
 * 
 * Funcionalidades:
 * - Controlo de Risco de Clientes
 * - Gestão de Fraudes Internas
 * - Análise por Loja
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, Users, Store, TrendingUp, TrendingDown,
  Lock, Unlock, DollarSign, Eye, UserX, AlertCircle, CheckCircle,
  FileText, History, Download
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDisplayCode, cn } from '@/lib/utils';
import GlobalFilters from '@/components/GlobalFilters';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { useAuth } from '@/hooks/useAuth';
import { getStoredAuthToken } from '@/lib/authStorage';
import { COMPACT_ACTION_BUTTONS } from '@/lib/tableDensity';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const riskAPI = {
  // Client Risk
  getClientRiskAssessment: () => api.get('/api/risk-management/clients/risk-assessment'),
  setClientCreditLimit: (data) => api.post('/api/risk-management/clients/credit-limit', data),
  getClientPaymentHistory: (id) => api.get(`/api/risk-management/clients/${id}/payment-history`),
  
  // Fraud
  getFraudAlerts: (params) => api.get('/api/risk-management/fraud/alerts', { params }),
  getSuspiciousActivities: (params) => api.get('/api/risk-management/fraud/suspicious-activities', { params }),
  investigateFraud: (id, data) => api.put(`/api/risk-management/fraud/${id}/investigate`, data),
  getAuditOperations: (params) => api.get('/api/risk-management/audit/operations', { params }),
  exportAuditOperationsPdf: (params) => api.get('/api/risk-management/audit/operations/pdf', { params, responseType: 'blob' }),
  
  // Location Performance
  getLocationPerformance: (params) => api.get('/api/risk-management/locations/performance', { params }),
  getLocationHealth: (id) => api.get(`/api/risk-management/locations/${id}/health`),
  exportLocationPerformancePdf: (params) => api.get('/api/risk-management/locations/performance/pdf', { params, responseType: 'blob' }),
  exportLocationPerformanceXlsx: (params) => api.get('/api/risk-management/locations/performance/xlsx', { params, responseType: 'blob' })
};

const RISK_PANEL = 'rounded-[28px] border border-slate-200/80 bg-white/95 shadow-[0_18px_48px_-28px_rgba(15,23,42,0.28)]';

const riskLevelColors = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  blocked: 'bg-red-100 text-red-800 border-red-300'
};

const riskLevelLabels = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  blocked: 'Bloqueado'
};

export default function RiskManagementPage() {
  const [activeTab, setActiveTab] = useState('clients');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [clientRisk, setClientRisk] = useState(null);
  const [suspiciousActivities, setSuspiciousActivities] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [locationPerformance, setLocationPerformance] = useState(null);
  const loadDataRef = useRef(null);
  const [exportingReport, setExportingReport] = useState('');
  
  // Dialogs
  const [creditLimitDialogOpen, setCreditLimitDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creditLimit, setCreditLimit] = useState('');
  const [selectedLocationHealth, setSelectedLocationHealth] = useState(null);
  const [locationDetailDialogOpen, setLocationDetailDialogOpen] = useState(false);
  const [loadingLocationDetailId, setLoadingLocationDetailId] = useState('');
  
  const { filters, setFilters } = useGlobalFilters({
    period: 'all'
  });
  
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);

  useEffect(() => {
    loadDataRef.current?.();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { period: filters.period };
      
      const [clientRes, suspiciousRes, fraudRes, auditRes, perfRes] = await Promise.all([
        riskAPI.getClientRiskAssessment(),
        riskAPI.getSuspiciousActivities(params),
        riskAPI.getFraudAlerts({ status: 'pending', period: filters.period }),
        riskAPI.getAuditOperations({ period: filters.period }),
        riskAPI.getLocationPerformance(params)
      ]);
      
      setClientRisk(clientRes.data);
      setSuspiciousActivities(suspiciousRes.data);
      setFraudAlerts(fraudRes.data || []);
      setAuditLogs(auditRes.data || []);
      setLocationPerformance(perfRes.data);
    } catch (error) {
      console.error('Failed to load risk data:', error);
      toast.error('Erro ao carregar dados de risco');
    } finally {
      setLoading(false);
    }
  };

  loadDataRef.current = loadData;

  const handleSetCreditLimit = async () => {
    if (!selectedClient || !creditLimit) return;
    
    try {
      await riskAPI.setClientCreditLimit({
        client_id: selectedClient.client_id,
        credit_limit: parseFloat(creditLimit)
      });
      toast.success('Limite de crédito definido');
      setCreditLimitDialogOpen(false);
      setSelectedClient(null);
      setCreditLimit('');
      loadData();
    } catch (error) {
      toast.error('Erro ao definir limite');
    }
  };

  const downloadPdfReport = async (reportKey, request, filename) => {
    try {
      setExportingReport(reportKey);
      const response = await request();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
      toast.success('PDF premium exportado com sucesso');
    } catch (error) {
      console.error('Risk PDF export error:', error);
      toast.error('Erro ao exportar PDF premium');
    } finally {
      setExportingReport('');
    }
  };

  const downloadSpreadsheetReport = async (reportKey, request, filename) => {
    try {
      setExportingReport(reportKey);
      const response = await request();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      console.error('Risk XLSX export error:', error);
      toast.error('Erro ao exportar Excel');
    } finally {
      setExportingReport('');
    }
  };

  const handleViewLocationDetails = async (locationId) => {
    try {
      setLoadingLocationDetailId(locationId);
      const response = await riskAPI.getLocationHealth(locationId);
      setSelectedLocationHealth(response.data);
      setLocationDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load location health:', error);
      toast.error('Erro ao carregar detalhes da loja');
    } finally {
      setLoadingLocationDetailId('');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="risk-management-page">
      {/* Header */}
      <div className={cn(RISK_PANEL, 'flex flex-col gap-4 p-6 lg:flex-row lg:items-end lg:justify-between')}>
        <div className="space-y-2">
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight" style={{fontFamily: 'Manrope'}}>
            <Shield className="h-8 w-8 text-red-600" />
            Gestão de Risco
          </h1>
          <p className="text-sm text-muted-foreground">
            Controlo de clientes, fraude e disciplina financeira
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-slate-600">
            <Badge variant="outline" className="rounded-full px-3 py-1">Período: {filters.period}</Badge>
            <Badge variant="outline" className="rounded-full px-3 py-1">Utilizador: {user?.full_name || user?.email || 'Sistema'}</Badge>
          </div>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilters 
        filters={['period']}
        onChange={(newFilters) => setFilters(newFilters)}
        defaultPeriod={filters.period}
      />

      {/* Summary KPIs */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Card className={cn(RISK_PANEL, 'border-l-4 border-l-red-500')}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Clientes Alto Risco</p>
            <p className="text-2xl font-bold text-red-600">{clientRisk?.summary?.high_risk || 0}</p>
          </CardContent>
        </Card>

        <Card className={cn(RISK_PANEL, 'border-l-4 border-l-orange-500')}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Clientes Bloqueados</p>
            <p className="text-2xl font-bold text-orange-600">{clientRisk?.summary?.blocked || 0}</p>
          </CardContent>
        </Card>

        <Card className={cn(RISK_PANEL, 'border-l-4 border-l-amber-500')}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Dívida Total</p>
            <p className="text-2xl font-bold text-amber-600">
              {formatCurrency(clientRisk?.summary?.total_outstanding_debt || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className={cn(RISK_PANEL, 'border-l-4 border-l-purple-500')}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Actividades Suspeitas</p>
            <p className="text-2xl font-bold text-purple-600">{suspiciousActivities?.total_suspicious || 0}</p>
          </CardContent>
        </Card>

        <Card className={cn(RISK_PANEL, 'border-l-4 border-l-blue-500')}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Lojas Deficitárias</p>
            <p className="text-2xl font-bold text-blue-600">
              {(locationPerformance?.warnings?.draining_locations || []).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 rounded-2xl border border-slate-200 bg-slate-100/90 p-1 lg:grid-cols-4">
          <TabsTrigger value="clients" data-testid="risk-tab-clients">Risco Clientes</TabsTrigger>
          <TabsTrigger value="fraud" data-testid="risk-tab-fraud">Fraude</TabsTrigger>
          <TabsTrigger value="locations" data-testid="risk-tab-locations">Lojas</TabsTrigger>
          <TabsTrigger value="audit" data-testid="risk-tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Avaliação de Risco de Clientes</CardTitle>
              <CardDescription>
                {clientRisk?.summary?.total_clients || 0} clientes | 
                Dívida total: {formatCurrency(clientRisk?.summary?.total_outstanding_debt || 0)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(clientRisk?.clients || []).filter(c => c.current_debt > 0 || c.risk_level !== 'low').length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhum cliente com risco</p>
              ) : (
                <div className="space-y-3">
                  {(clientRisk?.clients || [])
                    .filter(c => c.current_debt > 0 || c.risk_level !== 'low')
                    .slice(0, 20)
                    .map(client => (
                    <div key={client.client_id} className={cn(
                      "flex justify-between items-center p-3 border rounded-lg",
                      client.should_block && "border-red-300 bg-red-50"
                    )}>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          client.should_block ? "bg-red-100" : "bg-slate-100"
                        )}>
                          {client.should_block ? (
                            <Lock className="h-5 w-5 text-red-600" />
                          ) : (
                            <Users className="h-5 w-5 text-slate-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-[11px] font-medium">{client.client_name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {client.total_sales} compras | {client.late_payments} atrasos
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge className={cn("text-xs", riskLevelColors[client.risk_level])}>
                              {riskLevelLabels[client.risk_level]}
                            </Badge>
                            {client.credit_limit > 0 && (
                              <Badge variant="outline" className="text-xs">
                                Limite: {formatCurrency(client.credit_limit)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-red-600">
                          {formatCurrency(client.current_debt)}
                        </p>
                        {client.credit_limit > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {client.credit_used_pct.toFixed(0)}% do limite
                          </p>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`mt-2 ${COMPACT_ACTION_BUTTONS.tab}`}
                          onClick={() => {
                            setSelectedClient(client);
                            setCreditLimit(client.credit_limit?.toString() || '');
                            setCreditLimitDialogOpen(true);
                          }}
                        >
                          Definir Limite
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fraud Tab */}
        <TabsContent value="fraud" className="space-y-4 mt-4">
          {/* Suspicious Activities */}
          <Card className={cn(
            "border-2",
            (suspiciousActivities?.by_severity?.critical || 0) > 0 ? "border-red-300" : "border-slate-200"
          )}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Actividades Suspeitas
              </CardTitle>
              <div className="flex gap-2">
                <Badge className="bg-red-100 text-red-700">
                  Críticas: {suspiciousActivities?.by_severity?.critical || 0}
                </Badge>
                <Badge className="bg-orange-100 text-orange-700">
                  Altas: {suspiciousActivities?.by_severity?.high || 0}
                </Badge>
                <Badge className="bg-amber-100 text-amber-700">
                  Médias: {suspiciousActivities?.by_severity?.medium || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {(suspiciousActivities?.activities || []).length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">Nenhuma actividade suspeita detectada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(suspiciousActivities?.activities || []).map((activity, idx) => (
                    <div key={idx} className={cn(
                      "p-3 border rounded-lg",
                      activity.severity === 'critical' ? "border-red-300 bg-red-50" :
                      activity.severity === 'high' ? "border-orange-300 bg-orange-50" :
                      "border-amber-300 bg-amber-50"
                    )}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={cn(
                              "text-xs",
                              activity.severity === 'critical' ? "bg-red-100 text-red-700" :
                              activity.severity === 'high' ? "bg-orange-100 text-orange-700" :
                              "bg-amber-100 text-amber-700"
                            )}>
                              {activity.type}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{activity.severity}</Badge>
                          </div>
                          
                          {activity.type === 'high_discount' && (
                            <p className="text-[11px]">
                              Venda <strong>{formatDisplayCode(activity.sale_number, 'SAL')}</strong>: Desconto de {activity.discount_percentage}% 
                              ({formatCurrency(activity.discount_amount)})
                            </p>
                          )}
                          
                          {activity.type === 'cashbox_discrepancy' && (
                            <p className="text-[11px]">
                              Caixa <strong>{activity.location}</strong>: Diferença de {formatCurrency(activity.difference)}
                              <br />
                              Esperado: {formatCurrency(activity.expected)} | Real: {formatCurrency(activity.actual)}
                            </p>
                          )}
                          
                          {activity.type === 'void_sale' && (
                            <p className="text-[11px]">
                              Venda anulada: <strong>{formatDisplayCode(activity.sale_number, 'SAL')}</strong> ({formatCurrency(activity.total)})
                              <br />
                              Motivo: {activity.reason}
                            </p>
                          )}
                          
                          <p className="mt-1 text-[10px] text-muted-foreground">
                            Por: {activity.employee || activity.voided_by}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className={COMPACT_ACTION_BUTTONS.tab}>
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          Investigar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-4 space-y-4" data-testid="risk-locations-tab-content">
          <Card className={cn(RISK_PANEL, 'overflow-hidden')}>
            <CardHeader className="border-b border-slate-200/80 bg-[linear-gradient(135deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Store className="h-5 w-5 text-sky-600" />
                    Performance por Loja
                  </CardTitle>
                  <CardDescription>Relatório operacional no estilo actualizado do ERP, com leitura rápida e detalhe por unidade.</CardDescription>
                  {(locationPerformance?.warnings?.draining_locations || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-red-600" data-testid="risk-locations-warning-banner">
                      <AlertTriangle className="h-4 w-4" />
                      Lojas a drenar caixa: {locationPerformance.warnings.draining_locations.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadSpreadsheetReport(
                      'locations-xlsx',
                      () => riskAPI.exportLocationPerformanceXlsx({ period: filters.period }),
                      `desempenho_lojas_${new Date().toISOString().slice(0, 10)}.xlsx`
                    )}
                    disabled={exportingReport === 'locations-xlsx' || !locationPerformance}
                    data-testid="risk-performance-xlsx-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportingReport === 'locations-xlsx' ? 'A gerar Excel...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdfReport(
                      'locations',
                      () => riskAPI.exportLocationPerformancePdf({ period: filters.period }),
                      `desempenho_lojas_${new Date().toISOString().slice(0, 10)}.pdf`
                    )}
                    disabled={exportingReport === 'locations' || !locationPerformance}
                    data-testid="risk-performance-pdf-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportingReport === 'locations' ? 'A gerar PDF...' : 'PDF Premium'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5 p-5">
              <div className="grid gap-3 md:grid-cols-3" data-testid="risk-locations-summary-cards">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Receita total</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{formatCurrency(locationPerformance?.summary?.total_revenue || 0)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Lucro líquido</p>
                  <p className="mt-2 text-2xl font-semibold text-emerald-600">{formatCurrency(locationPerformance?.summary?.total_profit || 0)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Alertas de caixa</p>
                  <p className="mt-2 text-2xl font-semibold text-amber-600">{(locationPerformance?.warnings?.cashbox_issues || []).length}</p>
                </div>
              </div>

              {(locationPerformance?.locations || []).length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">Nenhuma loja encontrada</p>
              ) : (
                <div className="space-y-3" data-testid="risk-locations-report-list">
                  {(locationPerformance?.locations || []).map((loc) => (
                    <div
                      key={loc.location_id}
                      className={cn(
                        'rounded-3xl border p-3 transition-colors lg:p-4',
                        loc.status?.is_draining_cash && 'border-red-300 bg-red-50/75',
                        loc.status?.has_cashbox_issues && !loc.status?.is_draining_cash && 'border-amber-300 bg-amber-50/70',
                        !loc.status?.is_draining_cash && !loc.status?.has_cashbox_issues && 'border-slate-200 bg-white'
                      )}
                      data-testid={`risk-location-card-${loc.location_id}`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Store className={cn('h-5 w-5', loc.status?.is_profitable ? 'text-emerald-600' : 'text-red-600')} />
                            <p className="text-lg font-semibold text-slate-900">{loc.location_name}</p>
                            <Badge className={loc.status?.is_profitable ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {loc.status?.is_profitable ? 'Lucrativa' : 'Deficitária'}
                            </Badge>
                            {loc.status?.has_cashbox_issues && <Badge className="bg-amber-100 text-amber-700">Problemas Caixa</Badge>}
                          </div>
                          <div className="grid gap-2 text-[11px] sm:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Vendas</p>
                              <p className="font-semibold text-slate-900">{formatCurrency(loc.metrics?.sales?.revenue || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Serviços</p>
                              <p className="font-semibold text-slate-900">{formatCurrency(loc.metrics?.services?.revenue || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Gaming</p>
                              <p className="font-semibold text-slate-900">{formatCurrency(loc.metrics?.gaming?.revenue || 0)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Despesas</p>
                              <p className="font-semibold text-red-600">{formatCurrency(loc.financials?.expenses || 0)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200/80 bg-white/80 p-3 lg:min-w-[220px] lg:items-end">
                          <div className="text-left lg:text-right">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Lucro líquido</p>
                            <p className={cn('text-2xl font-semibold', (loc.financials?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                              {formatCurrency(loc.financials?.net_profit || 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">Margem: {(loc.financials?.margin_pct || 0).toFixed(1)}%</p>
                          </div>
                          <div className="w-full space-y-1 text-[11px]">
                            <div className="flex justify-between gap-3">
                              <span className="text-muted-foreground">Diferença caixa</span>
                              <span className={cn('font-medium', (loc.cashbox?.total_discrepancy || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>{formatCurrency(loc.cashbox?.total_discrepancy || 0)}</span>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span className="text-muted-foreground">Sessões fechadas</span>
                              <span className="font-medium text-slate-900">{loc.cashbox?.sessions || 0}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`${COMPACT_ACTION_BUTTONS.tab} w-full lg:w-auto`}
                            onClick={() => handleViewLocationDetails(loc.location_id)}
                            disabled={loadingLocationDetailId === loc.location_id}
                            data-testid={`risk-location-details-button-${loc.location_id}`}
                          >
                            <Eye className="mr-2 h-3.5 w-3.5" />
                            {loadingLocationDetailId === loc.location_id ? 'A abrir...' : 'Ver detalhes'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Log de Auditoria
                  </CardTitle>
                  <CardDescription>Operações sensíveis registadas</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPdfReport(
                    'audit',
                    () => riskAPI.exportAuditOperationsPdf({ period: filters.period }),
                    `operacoes_sensiveis_${new Date().toISOString().slice(0, 10)}.pdf`
                  )}
                  disabled={exportingReport === 'audit' || auditLogs.length === 0}
                  data-testid="risk-audit-operations-pdf-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingReport === 'audit' ? 'A gerar PDF...' : 'PDF Premium'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma operação registada</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {auditLogs.slice(0, 50).map((log, idx) => (
                    <div key={log.id || idx} className="flex items-center gap-3 rounded border p-2.5">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-[11px] font-medium">{log.action}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {log.entity_type} | Por: {log.performed_by}
                        </p>
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {log.performed_at ? new Date(log.performed_at).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' }) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={locationDetailDialogOpen} onOpenChange={setLocationDetailDialogOpen}>
        <DialogContent className="w-[min(92vw,1100px)] max-w-[1100px] max-h-[88vh] overflow-y-auto" data-testid="risk-location-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selectedLocationHealth?.location?.name || 'Detalhes da Loja'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-3 md:grid-cols-3" data-testid="risk-location-detail-summary">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Tendência</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{selectedLocationHealth?.health?.revenue_trend || 'stable'}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Média diária</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{formatCurrency(selectedLocationHealth?.health?.daily_average || 0)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Alertas activos</p>
                <p className="mt-2 text-lg font-semibold text-red-600">{selectedLocationHealth?.health?.active_alerts || 0}</p>
              </div>
            </div>

            <Card className={RISK_PANEL}>
              <CardHeader>
                <CardTitle className="text-base">Receita diária recente</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.entries(selectedLocationHealth?.daily_revenue || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem dados recentes para esta loja.</p>
                ) : (
                  <div className="space-y-2" data-testid="risk-location-daily-revenue-list">
                    {Object.entries(selectedLocationHealth?.daily_revenue || {}).slice(-14).map(([day, amount]) => (
                      <div key={day} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
                        <span>{day}</span>
                        <span className="font-medium text-slate-900">{formatCurrency(amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={RISK_PANEL}>
              <CardHeader>
                <CardTitle className="text-base">Alertas activos da loja</CardTitle>
              </CardHeader>
              <CardContent>
                {(selectedLocationHealth?.alerts || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem alertas activos neste momento.</p>
                ) : (
                  <div className="space-y-2" data-testid="risk-location-alerts-list">
                    {(selectedLocationHealth?.alerts || []).map((alert) => (
                      <div key={alert.id} className="rounded-2xl border border-amber-200 bg-amber-50/80 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-slate-900">{alert.title || alert.rule_name || 'Alerta activo'}</p>
                          <Badge className="bg-amber-100 text-amber-700">{alert.severity || 'active'}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{alert.description || alert.message || 'Sem descrição adicional.'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit Limit Dialog */}
      <Dialog open={creditLimitDialogOpen} onOpenChange={setCreditLimitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Definir Limite de Crédito</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Cliente</Label>
              <p className="font-medium">{selectedClient?.client_name}</p>
              <p className="text-sm text-muted-foreground">
                Dívida actual: {formatCurrency(selectedClient?.current_debt || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Limite de Crédito (MZN)</Label>
              <Input 
                type="number"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditLimitDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSetCreditLimit}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
